import { CONFIG } from '@/config';
import type { ConfigOptions } from '@/config-hoc/types';
import { allMenus } from '@/menus/all.ts';
import { resolveRuntimeEffectiveUserPermissionCodes } from '@/menus/util.ts';
import type { TenantUsersEntity } from '@ts-max/car-export-api-sdk';
import type { PermissionCode } from '@ts-max/car-export-permission-code-sdk';
import { queryParse, Storage } from '@ts-max/utils';
import type { PaginationProps } from 'antd';
import type { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { match } from 'path-to-regexp';
import pagesConfig, { type PageConfig as BasePageConfig } from '~pages-config';

export * from './contract';
export * from './table-row-click';
export * from './text-template';

const AUTH_STATE_CHANGE_EVENT = 'car-export:auth-state-change';
const TENANT_STATE_CHANGE_EVENT = 'car-export:tenant-state-change';

/**
 * 本地存储二次封装，自动转json
 */
export const storage = {
  session: new Storage({ storage: sessionStorage }),
  local: new Storage({ storage: localStorage }),
};

/**
 * config 高阶组件类型
 * */
export type PageConfig = ConfigOptions & BasePageConfig;

/**
 * 根据路径名获取页面配置
 * @param pathname - 页面路径名，默认为当前窗口路径
 * @returns 页面配置对象
 */
export function getPageConfig(
  pathname: string = window.location.pathname,
): PageConfig {
  const _pathname = pathname.replace(`/${CONFIG.basename}`, '') || '/';

  if (!_pathname) return {} as PageConfig;

  const cfg = pagesConfig?.find((item) => {
    if (!item.routePath) return false;

    return match(item.routePath)(_pathname);
  });

  return (cfg || {}) as PageConfig;
}

/**
 * 检查用户是否已登录
 * @returns 如果用户已登录返回 true，否则返回 false
 */
export function isLogin() {
  return !!getLoginUser();
}

/**
 * 校验 redirect 是否同源，返回安全的同源 URL；否则返回 null。
 * 统一收口，防止开放重定向（Open Redirect）钓鱼攻击。
 *
 * 拦截的典型攻击向量：
 *   https://evil.com          ← 跨域绝对 URL
 *   //evil.com/path           ← 协议相对 URL
 *   javascript:alert(1)       ← JS 伪协议
 */
function validateSameOriginRedirect(
  redirect: string | undefined | null,
): string | null {
  if (!redirect) return null;
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    // 返回规范化后的 href，避免 path traversal 等边界情况
    return url.href;
  } catch {
    return null;
  }
}

/**
 * 跳转到登录页面
 * @param options - 跳转选项
 * @param options.response - Axios 响应对象
 * @param options.redirect - 登录后重定向的 URL（同源校验，非同源时忽略）
 */
export function toLogin(options?: {
  response?: AxiosResponse;
  redirect?: string;
}) {
  if (isLoginPath()) return;

  const loginPath = `/${CONFIG.basename}/login`;
  const loginUser = getLoginUser();

  if (loginUser && !loginUser.verified) {
    return toVerify();
  }

  // options?.redirect 为外部传入，必须同源校验；不合法则回落到当前页 URL
  const redirect =
    validateSameOriginRedirect(options?.redirect) ?? window.location.href;

  const loginUrl = `${loginPath}?redirect=${encodeURIComponent(redirect || `/${CONFIG.basename}`)}`;
  setLoginUser(null);
  setToken('');
  setPermission([]);
  return (window.location.href = loginUrl);
}

/**
 * 跳转到首页
 * 优先使用 URL 中的 redirect 参数，否则跳转到根路径
 * 同源校验：防止开放重定向攻击
 */
export function toHome() {
  const { redirect } = queryParse();
  const defaultRedirect = `/${CONFIG.basename}/`;
  const safeRedirect =
    validateSameOriginRedirect(
      typeof redirect === 'string' ? redirect : null,
    ) ?? defaultRedirect;
  return (window.location.href = safeRedirect);
}

/**
 * 跳转到验证页面
 * 如果用户未验证，会跳转到验证页面
 */
export function toVerify() {
  const verifyPath = `/${CONFIG.basename}/system/verify`;
  if (window.location.pathname.includes(verifyPath)) return;

  // URL 中的 redirect 参数为外部可控，必须同源校验
  const { redirect: rawRedirect } = queryParse();
  const rawStr = typeof rawRedirect === 'string' ? rawRedirect : null;

  // 同源校验；login 页面本身不做 redirect 目标（避免循环跳转）
  let redirect = validateSameOriginRedirect(rawStr) ?? window.location.href;

  if (isLoginPath(redirect)) redirect = `/${CONFIG.basename}/`;

  return (window.location.href = `${verifyPath}?redirect=${encodeURIComponent(redirect)}`);
}

/**
 * 检查路径是否为登录路径
 * @param path - 要检查的路径，默认为当前窗口路径
 * @returns 如果是登录路径返回 true，否则返回 false
 */
function isLoginPath(path = window.location.pathname) {
  // path 可能是完整 URL（https://host/admin/login?...）或纯 pathname，统一提取 pathname 再比较
  let pathname: string;
  try {
    pathname = new URL(path, window.location.origin).pathname;
  } catch {
    pathname = path;
  }
  return (
    pathname === `/${CONFIG.basename}/login` ||
    pathname.startsWith(`/${CONFIG.basename}/login/`)
  );
}

/**
 * 公共分页配置，避免每个页面都单独编写
 */
export const commonPagination: Partial<PaginationProps> = {
  size: 'small',
  showSizeChanger: true,
  pageSizeOptions: [10, 20, 50, 100],
  showQuickJumper: true,
  showTotal: (total: number) => t('common:total_count', { total }),
};

type ChineseUpperAmountValue = number | string | null | undefined;

export interface FormatChineseUpperAmountOptions {
  empty?: string;
  prefix?: string;
  /** 金额来源单位，默认按后端金额字段的分处理。 */
  sourceUnit?: 'cent' | 'yuan';
}

const chineseUpperDigits = [
  '零',
  '壹',
  '贰',
  '叁',
  '肆',
  '伍',
  '陆',
  '柒',
  '捌',
  '玖',
];

const chineseUpperIntegerUnits = ['仟', '佰', '拾', ''];
const chineseUpperSectionUnits = ['', '万', '亿', '兆'];

/**
 * 将金额转换为人民币大写，默认入参单位为分。
 */
export function formatChineseUpperAmount(
  value: ChineseUpperAmountValue,
  options: FormatChineseUpperAmountOptions = {},
) {
  const { empty = '-', prefix = '', sourceUnit = 'cent' } = options;
  if (value === undefined || value === null || value === '') return empty;

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return empty;

  const centAmount =
    sourceUnit === 'cent'
      ? Math.round(numberValue)
      : Math.round(numberValue * 100);
  const sign = centAmount < 0 ? '负' : '';
  const absCentAmount = Math.abs(centAmount);
  const integerPart = Math.floor(absCentAmount / 100);
  const fractionPart = absCentAmount % 100;
  const jiao = Math.floor(fractionPart / 10);
  const fen = fractionPart % 10;

  let result = `${prefix}${sign}${integerToChineseUpper(integerPart)}元`;
  if (jiao === 0 && fen === 0) return `${result}整`;

  if (jiao > 0) result += `${chineseUpperDigits[jiao]}角`;
  if (jiao === 0 && fen > 0) result += '零';
  if (fen > 0) result += `${chineseUpperDigits[fen]}分`;
  return result;
}

function integerToChineseUpper(value: number) {
  if (value === 0) return chineseUpperDigits[0];

  const groups: number[] = [];
  let current = Math.trunc(value);
  while (current > 0) {
    groups.unshift(current % 10000);
    current = Math.floor(current / 10000);
  }

  let result = '';
  groups.forEach((group, index) => {
    const sectionIndex = groups.length - index - 1;
    if (group === 0) {
      if (result && groups.slice(index + 1).some((item) => item > 0)) {
        result += chineseUpperDigits[0];
      }
      return;
    }

    if (result && group < 1000) result += chineseUpperDigits[0];
    result += `${sectionToChineseUpper(group)}${chineseUpperSectionUnits[sectionIndex]}`;
  });

  return result.replace(/零+/g, chineseUpperDigits[0]).replace(/零$/u, '');
}

function sectionToChineseUpper(value: number) {
  let result = '';
  let shouldAppendZero = false;

  chineseUpperIntegerUnits.forEach((unit, index) => {
    const divisor = 10 ** (3 - index);
    const digit = Math.floor(value / divisor) % 10;
    if (digit === 0) {
      if (result) shouldAppendZero = true;
      return;
    }

    if (shouldAppendZero) {
      result += chineseUpperDigits[0];
      shouldAppendZero = false;
    }
    result += `${chineseUpperDigits[digit]}${unit}`;
  });

  return result;
}

/**
 * 登录用户类型
 */
export interface LoginUser {
  id: number | string;
  name: string;

  [key: string]: any;
}

export function setCurrentTenant(tenant: TenantUsersEntity) {
  storage.session.setItem('currentTenant', tenant);
  emitTenantStateChange();
}

export function getCurrentTenant(): TenantUsersEntity {
  return storage.session.getItem('currentTenant') as TenantUsersEntity;
}

export function subscribeTenantStateChange(listener: () => void): () => void {
  window.addEventListener(TENANT_STATE_CHANGE_EVENT, listener);
  return () => window.removeEventListener(TENANT_STATE_CHANGE_EVENT, listener);
}
/**
 * 获取当前登录用户信息
 * @returns 登录用户对象，如果未登录返回 null
 */
export function getLoginUser(): null | LoginUser {
  return storage.local.getItem('loginUser');
}

/**
 * 设置登录用户信息
 * @param user - 登录用户对象
 */
export function setLoginUser(user: LoginUser | null): void {
  storage.local.setItem('loginUser', user);
  emitAuthStateChange();
}

/**
 * 设置访问令牌
 * @param token - 访问令牌字符串
 */
export function setToken(token: string): void {
  storage.session.setItem('accessToken', token);
  emitAuthStateChange();
}

/**
 * 获取访问令牌
 * @returns 访问令牌字符串
 */
export function getToken(): string | null {
  return storage.session.getItem('accessToken');
}

/**
 * 获取用户权限列表
 * @returns 权限代码数组，如果没有权限列表返回 null
 */
export function getPermission(): null | string[] {
  return storage.session.getItem('permission');
}

/**
 * 设置用户权限列表
 * @param permission - 权限代码数组
 */
export function setPermission(permission: string[]): void {
  storage.session.setItem(
    'permission',
    resolveRuntimeEffectiveUserPermissionCodes(permission, allMenus),
  );
}

export function subscribeAuthStateChange(listener: () => void): () => void {
  window.addEventListener(AUTH_STATE_CHANGE_EVENT, listener);
  return () => window.removeEventListener(AUTH_STATE_CHANGE_EVENT, listener);
}

function emitAuthStateChange(): void {
  window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
}

function emitTenantStateChange(): void {
  window.dispatchEvent(new Event(TENANT_STATE_CHANGE_EVENT));
}

/**
 * 检查用户是否具有指定权限
 * @param code - 权限代码，可以是字符串、字符串数组或布尔值
 *   - 字符串：检查单个权限
 *   - 字符串数组：检查是否有任一权限（OR 逻辑）
 *   - 布尔值：直接返回该值（true 表示有权限，false 表示无权限）
 * @returns 如果有权限返回 true，否则返回 false
 */
export function hasPermission(
  code: PermissionCode | PermissionCode[] | boolean,
): boolean {
  if (!code) return false;

  if (typeof code === 'boolean') return code;

  const userPermission = getPermission();

  if (!userPermission?.length) return false;

  // 处理字符串类型：检查是否在权限列表中
  if (typeof code === 'string') {
    return userPermission.includes(code);
  }

  // 处理数组类型：只要有一个权限匹配就返回 true
  if (Array.isArray(code)) {
    return code.some((item) => userPermission.includes(item));
  }

  return false;
}

export const isInIframe = window.self !== window.top;

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    platform?: string;
  };
};

export function isAppleSystem(): boolean {
  if (typeof navigator === 'undefined') return false;

  const nav = navigator as NavigatorWithUserAgentData;
  const platform = nav.userAgentData?.platform || nav.userAgent;
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/**
 * 获取当前企业代码
 * url第一层为企业代码
 */
export function getCurrentTenantCode(): string {
  const pathname = window.location.pathname;
  return pathname.split('/').filter(Boolean)[0];
}

export function download(url: string, fileName?: string) {
  // 创建临时下载链接
  const link = document.createElement('a');
  link.href = url;

  if (fileName) link.download = fileName;

  // 添加链接到DOM并触发点击事件
  document.body.appendChild(link);
  link.click();
  // 清理临时元素和URL对象
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
}
